import React, { useEffect, useState } from "react";
import { useSidebar } from "../../context/SidebarContext";
import AttendanceTable from "../../components/StaffPortal/AttendanceTable";
import { attendanceAPI } from "../../services/AdminPortal/staffworkfolioAPI";
import { useAuth } from "../../context/AuthContext";
import LeaveRequestDialog from "../../components/StaffPortal/LeaveRequestDialog";
import {
  convertDecimalToTime,
  formatDate,
  formatDateDetailed,
  formatElapsed,
  formatTime,
  formatDurationFromMinutes,
} from "../../utils/utils";
import { toast } from "react-toastify";
import { CalendarDays, CalendarDaysIcon } from "lucide-react";

const Attendance = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("Not Clocked In"); // Set default status
  const [currentStatusColor, setCurrentStatusColor] = useState("text-red-500"); // Default color
  const { user } = useAuth();
  const { staffId } = user;
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  
  // Initialize timer state variables early
  const [elapsed, setElapsed] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [isClockedOut, setIsClockedOut] = useState(false);
  
  const getToday = () => new Date().toISOString().split("T")[0];

  const openLeaveDialog = () => {
    setIsLeaveDialogOpen(true);
  };

  const closeLeaveDialog = () => {
    setIsLeaveDialogOpen(false);
  };

  // Helper function to render status with color
  const renderStatus = (status, colorClass) => {
    return <p className={`${colorClass} text-sm`}>{status}</p>;
  };

  const formatTimeAsDigital = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return [hours, minutes, secs].map((v) => (v < 10 ? "0" + v : v)).join(":");
  };

  useEffect(() => {
    if (!staffId) return;
    
    const fetchAttendanceData = async () => {
      try {
        const data = await attendanceAPI.getAttendance(staffId);
        setAttendanceData(data);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };
    
    const fetchCurrentStatus = async () => {
      try {
        const data = await attendanceAPI.getCurrentStaffStatus(staffId);
        const status = data.status;
        setCurrentStatus(status);
        
        // Update UI state based on backend status
        if (status === "working" || status === "logged-in") {
          setIsRunning(true);
          setIsOnBreak(false);
          setIsClockedOut(false);
          setCurrentStatusColor("text-green-500");
          
          // Update localStorage to match backend state
          const saved = JSON.parse(localStorage.getItem(`workTimer_${staffId}`)) || {};
          persist({
            isRunning: true,
            isOnBreak: false,
            isClockedOut: false,
            startTime: saved.startTime || Date.now(),
            elapsed: saved.elapsed || 0
          });
        } else if (status === "in-break") {
          setIsRunning(true);
          setIsOnBreak(true);
          setIsClockedOut(false);
          setCurrentStatusColor("text-yellow-500");
          
          // Update localStorage to match backend state
          persist({
            isRunning: true,
            isOnBreak: true,
            isClockedOut: false
          });
        } else if (status === "Clocked-out" || status === "logged-out") {
          setIsRunning(false);
          setIsOnBreak(false);
          setIsClockedOut(true);
          setCurrentStatusColor("text-red-500");
          
          // Update localStorage to match backend state
          persist({
            isRunning: false,
            isOnBreak: false,
            isClockedOut: true
          });
        } else {
          setCurrentStatusColor("text-gray-500"); // Fallback color
        }
      } catch (error) {
        console.error("Error fetching current status:", error);
        // Handle error: API might fail if no clock-in record for today
        // Fall back to localStorage if available
        const saved = JSON.parse(localStorage.getItem(`workTimer_${staffId}`)) || {};
        const today = getToday();
        
        if (saved.date === today) {
          // Use localStorage state if it's from today
          if (saved.isRunning && !saved.isClockedOut) {
            if (saved.isOnBreak) {
              setCurrentStatus("in-break");
              setCurrentStatusColor("text-yellow-500");
            } else {
              setCurrentStatus("logged-in");
              setCurrentStatusColor("text-green-500");
            }
          } else if (saved.isClockedOut) {
            setCurrentStatus("logged-out");
            setCurrentStatusColor("text-red-500");
          } else {
            setCurrentStatus("No Clock-in Today");
            setCurrentStatusColor("text-red-500");
          }
        } else {
          setCurrentStatus("No Clock-in Today");
          setCurrentStatusColor("text-red-500");
        }
      }
    };
    
    fetchCurrentStatus();
    fetchAttendanceData();
  }, [staffId]);

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer); // cleanup
  }, []);

  const today = getToday(); // Get today in YYYY-MM-DD

  // Filter data to only include records for today
  const currentData = attendanceData.filter((item) => {
    const itemDate = new Date(item.date).toISOString().split("T")[0];
    return itemDate === today;
  });
  const todayData = currentData[0] || {}; // Default to zero if no data

  // Initialize state from localStorage on component mount
  useEffect(() => {
    if (!staffId) return;
    
    // Check localStorage first for initial state
    const initializeFromLocalStorage = () => {
      const saved = JSON.parse(localStorage.getItem(`workTimer_${staffId}`)) || {};
      const today = getToday();
      
      // Only use localStorage data if it's from today
      if (saved.date === today) {
        const now = Date.now();
        
        // Set the state based on localStorage
        setIsRunning(saved.isRunning || false);
        setIsOnBreak(saved.isOnBreak || false);
        setIsClockedOut(saved.isClockedOut || false);
        
        // Calculate elapsed time including time that passed while logged out
        if (saved.isRunning && !saved.isOnBreak && !saved.isClockedOut) {
          if (saved.startTime) {
            const timeSinceStart = Math.floor((now - saved.startTime) / 1000);
            setElapsed(saved.elapsed + timeSinceStart);
            
            // Update the startTime to now to prevent double-counting
            persist({
              elapsed: saved.elapsed + timeSinceStart,
              startTime: now,
              isRunning: true,
              isOnBreak: false,
              isClockedOut: false
            });
          } else {
            setElapsed(saved.elapsed || 0);
          }
        } else {
          setElapsed(saved.elapsed || 0);
        }
      }
    };
    
    // Initialize immediately to prevent flashing of incorrect state
    initializeFromLocalStorage();
  }, [staffId]);

  // Save state to localStorage
  const persist = React.useCallback((newState = {}) => {
    if (!staffId) return;
    
    const state = {
      date: getToday(),
      elapsed,
      isRunning,
      isOnBreak,
      isClockedOut,
      startTime: isRunning ? Date.now() : null,
      ...newState,
    };
    localStorage.setItem(`workTimer_${staffId}`, JSON.stringify(state));
  }, [staffId, elapsed, isRunning, isOnBreak, isClockedOut]);

  const handleSubmitLeaveRequest = async (leaveData) => {
    try {
      const response = await attendanceAPI.createLeaveRequest(
        staffId,
        leaveData
      );

      console.log("Leave request submitted successfully:", response);
      toast.success("Leave request submitted successfully");

      closeLeaveDialog();
    } catch (error) {
      console.error("Error submitting leave request:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to submit leave request"
      );
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (isRunning && !isOnBreak && !isClockedOut) {
      interval = setInterval(() => {
        setElapsed((prev) => {
          const updated = prev + 1;
          const storageKey = `workTimer_${staffId}`;
          localStorage.setItem(
            storageKey,
            JSON.stringify({
              ...JSON.parse(localStorage.getItem(storageKey) || "{}"),
              elapsed: updated,
            })
          );
          return updated;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, isOnBreak, isClockedOut, staffId]);

  // Button handlers
  const handleClockIn = async () => {
    try {
      await attendanceAPI.clockIn(staffId);
      
      // Reset the timer when clocking in
      setElapsed(0);
      setIsRunning(true);
      setIsOnBreak(false);
      setIsClockedOut(false);
      
      // Update localStorage with fresh state
      persist({ 
        isRunning: true, 
        isOnBreak: false, 
        isClockedOut: false, 
        startTime: Date.now(),
        elapsed: 0 
      });
      
      setCurrentStatus("logged-in");
      setCurrentStatusColor("text-green-500");
      
      // Refresh attendance data
      const data = await attendanceAPI.getAttendance(staffId);
      setAttendanceData(data);
    } catch (error) {
      console.error("Error clocking in:", error.error);
      toast.error(`Failed to clock in: ${error?.error || "Unknown error"}`);
    }
  };

  const handleStartBreak = async () => {
    try {
      await attendanceAPI.takeBreak(staffId);
      setIsOnBreak(true);
      persist({ isRunning: true, isOnBreak: true, isClockedOut: false });
      setCurrentStatus("in-break");
      setCurrentStatusColor("text-yellow-500");
      
      // Refresh attendance data
      const data = await attendanceAPI.getAttendance(staffId);
      setAttendanceData(data);
    } catch (error) {
      console.error("Error starting break:", error);
      toast.error(
        `Failed to start break: ${error?.message || "Unknown error"}`
      );
    }
  };

  const handleEndBreak = async () => {
    try {
      await attendanceAPI.endBreak(staffId);
      setIsOnBreak(false);
      persist({ isRunning: true, isOnBreak: false, isClockedOut: false, startTime: Date.now() });
      setCurrentStatus("logged-in");
      setCurrentStatusColor("text-green-500");
      
      // Refresh attendance data
      const data = await attendanceAPI.getAttendance(staffId);
      setAttendanceData(data);
    } catch (error) {
      console.error("Error ending break:", error);
      toast.error(
        `Failed to end break: ${error?.message || "Unknown error"}`
      );
    }
  };

  const handleClockOut = async () => {
    try {
      await attendanceAPI.clockOut(staffId);
      setIsClockedOut(true);
      setIsRunning(false);
      persist({ isRunning: false, isOnBreak: false, isClockedOut: true });
      setCurrentStatus("logged-out");
      setCurrentStatusColor("text-red-500");
      
      // Refresh attendance data
      const data = await attendanceAPI.getAttendance(staffId);
      setAttendanceData(data);
    } catch (error) {
      console.error("Error clocking out:", error);
      toast.error(
        `Failed to clock out: ${error?.message || "Unknown error"}`
      );
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-white   mt-12 p-4 md:p-6"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6 w-full text-center sm:text-left">
        <h1 className="text-2xl sm:text-2xl font-medium w-full sm:w-auto">
          Attendance Management
        </h1>
        <button
          onClick={openLeaveDialog}
          className="px-3 py-1.5 sm:px-4 sm:py-2 border-2 border-yellow-500 text-xs sm:text-sm text-black bg-[#ffca20] rounded flex items-center gap-2 justify-center w-full sm:w-auto hover:bg-yellow-300 transition"
        >
          <span>Request Leave</span>
          <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>

      <div className="flex flex-col md:flex-row flex-wrap gap-4">
        {/* Left Column */}
        <div className="bg-[#131313] rounded-lg p-4 w-full md:w-1/2 flex-1">
          <div>
            <div className="flex flex-row sm:flex-row justify-between items-start sm:items-center mb-4">
              <div className="mb-2 sm:mb-0">
                <p className="text-3xl">{formatTime(currentTime)}</p>
                <p className="text-white/90 text-lg">
                  {formatDateDetailed(currentTime)}
                </p>
              </div>
              <div className="bg-[#333333] p-3 rounded text-center">
                <p className="text-sm font-semibold">STATUS</p>
                {renderStatus(currentStatus, currentStatusColor)}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <div className="flex flex-col sm:flex-row w-full">
                {/* Row 1: Clock In/Out buttons */}
                <div className="w-full sm:w-auto mb-2 sm:mb-0">
                  {(!isRunning || isClockedOut) && (
                    <div>
                      <button
                        onClick={handleClockIn}
                        variant="default"
                        className="bg-[#FFCA20] text-[#131313] hover:bg-gray-700 hover:text-[#FFCA20] border-2 border-[#FFCA20] rounded py-2 px-4 text-sm transition duration-300 w-full sm:w-auto"
                      >
                        Clock In
                      </button>
                      <div className="mt-2 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-400">
                        <span className="font-medium">Note:</span> Monthly only 2 leaves allowed
                      </div>
                    </div>
                  )}
                  {isRunning && !isClockedOut && (
                    <div>
                      <button
                        onClick={handleClockOut}
                        variant="default"
                        className="bg-[#FFCA20] text-[#131313] hover:bg-gray-700 hover:text-[#FFCA20] border-2 border-[#FFCA20] rounded py-2 px-4 text-sm transition duration-300 w-full sm:w-auto"
                      >
                        Clock Out
                      </button>
                      <div className="mt-2 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-400">
                        <span className="font-medium">Note:</span> Monthly only 2 leaves allowed
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Row 2: Break buttons (mobile only) */}
                <div className="w-full sm:hidden mb-2">
                  {isRunning && !isOnBreak && !isClockedOut && (
                    <button
                      onClick={handleStartBreak}
                      variant="outline"
                      className="bg-[#131313] text-[#FFCA20] hover:bg-[#FFCA20] hover:text-[#131313] border-2 border-[#131313] rounded py-2 px-4 text-sm transition duration-300 w-full"
                    >
                      Take a Break
                    </button>
                  )}
                  {isOnBreak && !isClockedOut && (
                    <button
                      onClick={handleEndBreak}
                      variant="outline"
                      className="bg-[#131313] text-[#FFCA20] hover:bg-[#FFCA20] hover:text-[#131313] border-2 border-[#131313] rounded py-2 px-4 text-sm transition duration-300 w-full"
                    >
                      End Break
                    </button>
                  )}
                </div>
                
                {/* Break buttons for desktop */}
                <div className="hidden sm:flex gap-2 ml-4">
                  {isRunning && !isOnBreak && !isClockedOut && (
                    <button
                      onClick={handleStartBreak}
                      variant="outline"
                      className="bg-[#131313] text-[#FFCA20] hover:bg-[#FFCA20] hover:text-[#131313] border-2 border-[#131313] rounded py-2 px-4 text-sm transition duration-300"
                    >
                      Take a Break
                    </button>
                  )}
                  {isOnBreak && !isClockedOut && (
                    <button
                      onClick={handleEndBreak}
                      variant="outline"
                      className="bg-[#131313] text-[#FFCA20] hover:bg-[#FFCA20] hover:text-[#131313] border-2 border-[#131313] rounded py-2 px-4 text-sm transition duration-300"
                    >
                      End Break
                    </button>
                  )}
                </div>
                
                {/* Row 3: Digital timer */}
                <div className="text-center sm:ml-auto sm:text-right">
                  <p className="text-xl sm:text-2xl">
                    {formatTimeAsDigital(elapsed)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className="bg-[#131313] p-4 rounded-lg w-full md:w-1/4">
          <p className="text-sm font-semibold mb-3">Today's Overview</p>
          <div className="mb-3">
            <p className="font-medium text-sm">Hours Worked</p>
            <p className="text-sm">
              {convertDecimalToTime(todayData.regularHours)}
            </p>
          </div>
          <div>
            <p className="font-medium text-sm">Break Time</p>
            <p className="text-sm">
              {formatDurationFromMinutes(todayData.breakTime)}
            </p>
          </div>
        </div>

        {/* Right Column */}
        <div className="bg-[#131313] p-4 rounded-lg w-full md:w-1/4">
          <p className="text-sm font-semibold mb-3">Recent Activity</p>
          <div className="mb-2">
            <p className="text-sm">Clock In</p>
            <p className="text-gray-400 text-xs">
              {formatTime(todayData.loginTime)}
            </p>
          </div>
          <div className="mb-2">
            <p className="text-sm">Break Taken</p>
            <p className="text-gray-400 text-xs">
              {formatDurationFromMinutes(todayData.breakTime)}
            </p>
          </div>
          <div>
            <p className="text-sm">Clock Out</p>
            <p className="text-gray-400 text-xs">
              {formatTime(todayData.logoutTime)}
            </p>
          </div>
        </div>
      </div>

      {/* Attendance History Table */}
      <AttendanceTable data={attendanceData} />
      <LeaveRequestDialog
        isOpen={isLeaveDialogOpen}
        onClose={closeLeaveDialog}
        onSubmit={handleSubmitLeaveRequest} // Pass the submit handler
      />
    </div>
  );
};

export default Attendance;
